import React, { useMemo, useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import { Link } from "react-router-dom";
import { useI18n } from "../contexts/I18nContext";
import {
  ArrowRight, ExternalLink, Share2, Apple, Smartphone, Plus, MoreVertical, Sparkles, Check, Download
} from "lucide-react";

function normalize(u) {
  const s = (u || "").trim();
  if (!s) return "";
  return /^https?:\/\//i.test(s) ? s : `https://${s}`;
}

export default function SimpleMode() {
  const { t, isRTL } = useI18n();
  const [raw, setRaw] = useState("");
  const [submitted, setSubmitted] = useState("");
  const [copied, setCopied] = useState(false);

  const url = useMemo(() => normalize(submitted), [submitted]);
  const host = useMemo(() => {
    try { return new URL(url).hostname.replace("www.", ""); }
    catch { return ""; }
  }, [url]);

  const onGenerate = (e) => {
    e.preventDefault();
    const n = normalize(raw);
    if (!n) return;
    setSubmitted(n);
  };

  const onShare = async () => {
    if (!url) return;
    const data = { title: host || "Install", text: t("quick_subtitle"), url };
    if (navigator.share) {
      try { await navigator.share(data); return; } catch {}
    }
    try { await navigator.clipboard.writeText(url); } catch {}
    setCopied(true);
    setTimeout(() => setCopied(false), 1800);
  };

  const onDownload = () => {
    const svg = document.querySelector('[data-qr-svg="1"] svg');
    if (!svg) return;
    const xml = new XMLSerializer().serializeToString(svg);
    const blob = new Blob([xml], { type: "image/svg+xml" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `${host || "qr"}-trustconapp.svg`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  return (
    <div className="relative min-h-[80vh]">
      <div className="aurora opacity-50" />

      <div className="relative max-w-5xl mx-auto px-4 sm:px-6 py-14 z-10">
        <div className="text-center mb-10">
          <span className="chip">
            <Sparkles className="w-3.5 h-3.5" strokeWidth={2} />
            {t("home_quick_chip")}
          </span>
          <h1 className="font-display text-4xl sm:text-6xl font-bold tracking-tight mt-5 leading-[1.2]" data-testid="quick-title">
            {t("quick_title")}
          </h1>
          <p className="mt-4 text-zinc-600 max-w-md mx-auto">{t("quick_subtitle")}</p>
        </div>

        <form onSubmit={onGenerate} className="surface-card p-5 sm:p-6 max-w-2xl mx-auto" data-testid="quick-form">
          <label className="input-label">{t("quick_url_label")}</label>
          <div className="flex flex-col sm:flex-row gap-2">
            <input
              type="text"
              value={raw}
              onChange={(e) => setRaw(e.target.value)}
              placeholder={t("quick_url_placeholder")}
              className="input flex-1 font-mono text-sm"
              data-testid="quick-url"
              required
            />
            <button type="submit" className="btn btn-primary" data-testid="quick-generate">
              {t("quick_generate")}
              <ArrowRight className={`w-4 h-4 ${isRTL ? "rotate-180" : ""}`} strokeWidth={2} />
            </button>
          </div>
        </form>

        {url && (
          <div className="mt-10 grid lg:grid-cols-2 gap-6">
            {/* QR + actions */}
            <div className="surface-card p-7 text-center" data-testid="quick-result">
              <p className="text-xs font-mono text-zinc-400 mb-2 truncate">{host}</p>
              <div className="surface-card bg-white p-4 inline-block" data-qr-svg="1">
                <QRCodeSVG value={url} size={196} bgColor="#FFFFFF" fgColor="#0A0A0A" includeMargin={false} />
              </div>
              <p className="mt-4 text-xs uppercase tracking-[0.16em] text-zinc-500 font-medium">
                {t("install_sub")}
              </p>

              <div className="mt-6 flex flex-wrap justify-center gap-2">
                <a
                  href={url}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="quick-open"
                  className="btn btn-primary"
                >
                  <ExternalLink className="w-4 h-4" strokeWidth={2} />
                  {t("quick_open")}
                </a>
                <button
                  type="button"
                  onClick={onShare}
                  data-testid="quick-share"
                  className="btn btn-secondary"
                >
                  {copied ? <Check className="w-4 h-4" strokeWidth={2.5} /> : <Share2 className="w-4 h-4" strokeWidth={2} />}
                  {copied ? t("quick_share_copied") : t("quick_share")}
                </button>
                <button
                  type="button"
                  onClick={onDownload}
                  data-testid="quick-download"
                  className="btn btn-secondary"
                >
                  <Download className="w-4 h-4" strokeWidth={2} />
                  {t("quick_download")}
                </button>
              </div>
            </div>

            {/* Instructions */}
            <div className="space-y-4">
              <div className="surface-card p-6" data-testid="quick-ios-block">
                <h3 className="font-display font-semibold text-lg flex items-center gap-2 mb-4">
                  <Apple className="w-5 h-5" strokeWidth={1.6} />
                  {t("install_ios")}
                </h3>
                <ol className="space-y-3 text-sm">
                  <li className="flex items-start gap-3">
                    <span className="w-6 h-6 rounded-full bg-zinc-100 grid place-items-center font-medium text-xs flex-shrink-0">1</span>
                    <span className="flex items-center gap-1.5 text-zinc-700">{t("install_ios_1")}</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-6 h-6 rounded-full bg-zinc-100 grid place-items-center font-medium text-xs flex-shrink-0">2</span>
                    <span className="flex items-center gap-1.5 text-zinc-700">{t("install_ios_2")} <Plus className="w-3.5 h-3.5" strokeWidth={1.8} /></span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-6 h-6 rounded-full bg-zinc-100 grid place-items-center font-medium text-xs flex-shrink-0">3</span>
                    <span className="text-zinc-700">{t("install_ios_3")}</span>
                  </li>
                </ol>
              </div>

              <div className="surface-card p-6" data-testid="quick-android-block">
                <h3 className="font-display font-semibold text-lg flex items-center gap-2 mb-4">
                  <Smartphone className="w-5 h-5" strokeWidth={1.6} />
                  {t("install_android")}
                </h3>
                <ol className="space-y-3 text-sm">
                  <li className="flex items-start gap-3">
                    <span className="w-6 h-6 rounded-full bg-zinc-100 grid place-items-center font-medium text-xs flex-shrink-0">1</span>
                    <span className="flex items-center gap-1.5 text-zinc-700">{t("install_android_1")} <MoreVertical className="w-3.5 h-3.5" strokeWidth={1.8} /></span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-6 h-6 rounded-full bg-zinc-100 grid place-items-center font-medium text-xs flex-shrink-0">2</span>
                    <span className="text-zinc-700">{t("install_android_2")}</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-6 h-6 rounded-full bg-zinc-100 grid place-items-center font-medium text-xs flex-shrink-0">3</span>
                    <span className="text-zinc-700">{t("install_android_3")}</span>
                  </li>
                </ol>
              </div>

              <div className="surface-card p-5 text-center bg-zinc-50/60">
                <p className="text-sm text-zinc-600 mb-3">
                  Want a real PWA with custom icon &amp; install button?
                </p>
                <Link to="/register" data-testid="quick-upgrade" className="btn btn-primary btn-sm">
                  {t("home_build_card_cta")}
                  <ArrowRight className={`w-3.5 h-3.5 ${isRTL ? "rotate-180" : ""}`} strokeWidth={2} />
                </Link>
              </div>
            </div>
          </div>
        )}

        {!url && (
          <p className="mt-8 text-center text-sm text-zinc-400">{t("quick_no_url")}</p>
        )}
      </div>
    </div>
  );
}
